import { useEffect, useMemo, useRef, useState } from 'react';

import type { MeResponse, ProjectDto, TaskDto } from '@time-tracking/shared';

import { useProjectMembers, useTaskAssignments } from '../../../api/projects.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';
import { useSearchUsers } from '../../../api/users.js';

type TeamOption = MeResponse['teams'][number];

const AVATAR_COLORS = [
  { bg: '#e8f3e0', fg: '#2a6017' },
  { bg: '#e3eef5', fg: '#005f86' },
  { bg: '#fff6e0', fg: '#7a5a00' },
  { bg: '#f0e8f3', fg: '#6d3b8e' },
  { bg: '#ffe8e3', fg: '#b03000' },
  { bg: '#e6ebee', fg: '#333f48' },
];

function avatarColor(userId: string) {
  const sum = [...userId].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length] ?? { bg: '#e6ebee', fg: '#333f48' };
}

/**
 * A person as picked from a search result or member row, captured whole at selection
 * time. Holding the DTO's own initials means a pick from outside the loaded member list
 * still renders its avatar letters — there is no id to resolve back to a name later.
 */
interface PickedPerson {
  userId: string;
  displayName: string;
  initials: string;
}

interface AssignmentRowProps {
  userId: string;
  displayName: string;
  initials: string;
  subLabel: string;
  viaTeam: boolean;
  viaDirect: boolean;
  isProjectWide: boolean;
  isMarkedForRemoval: boolean;
  isSel: boolean;
  bg: string;
  fg: string;
  onToggle: (
    person: PickedPerson,
    viaTeam: boolean,
    viaDirect: boolean,
    isProjectWide?: boolean,
  ) => void;
}

function AssignmentRow({
  userId,
  displayName,
  initials,
  subLabel,
  viaTeam,
  viaDirect,
  isProjectWide,
  isMarkedForRemoval,
  isSel,
  bg,
  fg,
  onToggle,
}: AssignmentRowProps) {
  const disabled = viaTeam || isProjectWide;
  return (
    <div
      className={[
        'au-row',
        disabled ? 'au-row--on-already' : '',
        viaDirect && !isMarkedForRemoval && !isProjectWide ? 'au-row--on-already' : '',
        isMarkedForRemoval ? 'au-row--to-remove' : '',
        isSel ? 'au-row--selected' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="option"
      aria-selected={isSel || isMarkedForRemoval}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => onToggle({ userId, displayName, initials }, viaTeam, viaDirect, isProjectWide)}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          onToggle({ userId, displayName, initials }, viaTeam, viaDirect, isProjectWide);
        }
      }}
    >
      <span className="au-avatar" style={{ background: bg, color: fg }} aria-hidden="true">
        {initials}
      </span>
      <div className="au-who">
        <div className="au-who__name">{displayName}</div>
        <div className="au-who__team">{subLabel}</div>
      </div>
      <div className="au-status">
        {viaTeam ? (
          <span className="au-tag au-tag--via-team">
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M1.5 5.5l3 3 5-5" />
            </svg>
            on via team
          </span>
        ) : isProjectWide ? (
          <span
            className="au-tag au-tag--via-team"
            title="Assigned to the whole project — remove them from the Members tab"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M1.5 5.5l3 3 5-5" />
            </svg>
            on project
          </span>
        ) : isMarkedForRemoval ? (
          <span className="au-tag au-tag--to-remove">
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M1 1l9 9M10 1L1 10" />
            </svg>
            removing
          </span>
        ) : viaDirect ? (
          <span className="au-tag au-tag--via-direct">
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M1.5 5.5l3 3 5-5" />
            </svg>
            added · click to remove
          </span>
        ) : isSel ? (
          <span className="au-tag au-tag--picked">
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M1.5 5.5l3 3 5-5" />
            </svg>
            picked
          </span>
        ) : (
          <span className="au-tag au-tag--pickable">
            <svg
              width="11"
              height="11"
              viewBox="0 0 11 11"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M5.5 1v9M1 5.5h9" />
            </svg>
            add
          </span>
        )}
      </div>
    </div>
  );
}

interface AddAssignmentDialogProps {
  task: TaskDto | null;
  project: ProjectDto;
  teams: TeamOption[];
  totalTaskCount: number;
  onClose: () => void;
  onSubmit: (toAdd: string[], toRemoveAssignmentIds: string[]) => void;
  isPending: boolean;
}

export function AddAssignmentDialog({
  task,
  project,
  teams,
  totalTaskCount,
  onClose,
  onSubmit,
  isPending,
}: AddAssignmentDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const open = task !== null;
  useFocusTrap(dialogRef, open);

  const [query, setQuery] = useState('');
  // userId → the person as picked (see PickedPerson).
  const [selected, setSelected] = useState<ReadonlyMap<string, PickedPerson>>(new Map());
  const [toRemove, setToRemove] = useState<ReadonlySet<string>>(new Set());

  const membersQuery = useProjectMembers(open ? project.projectId : null);
  const assignmentsQuery = useTaskAssignments(open ? (task?.taskId ?? null) : null);

  const q = query.trim();
  const isSearchMode = q.length >= 1;
  const searchResult = useSearchUsers(q);

  const directAssignmentMap = useMemo(() => {
    const map = new Map<string, string>(); // userId → assignmentId
    for (const a of assignmentsQuery.data ?? []) {
      if (a.source === 'direct' && a.activeFlag) {
        map.set(a.assigneeUserId, a.assignmentId);
      }
    }
    return map;
  }, [assignmentsQuery.data]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setSelected(new Map());
    setToRemove(new Set());
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  const teamNameMap = useMemo(() => new Map(teams.map((t) => [t.teamId, t.name])), [teams]);

  const members = membersQuery.data ?? [];

  const membersById = useMemo(() => new Map(members.map((m) => [m.userId, m])), [members]);

  // Groups used when not in search mode (existing project-member view)
  const groups = useMemo(() => {
    const byTeam = new Map<string | null, typeof members>();
    for (const m of members) {
      const key = m.teamId;
      if (!byTeam.has(key)) byTeam.set(key, []);
      byTeam.get(key)?.push(m);
    }
    return [...byTeam.entries()].sort(([a], [b]) => {
      if (a === null) return 1;
      if (b === null) return -1;
      return 0;
    });
  }, [members]);

  function toggle(
    person: PickedPerson,
    viaTeam: boolean,
    viaDirect: boolean,
    isProjectWide = false,
  ) {
    if (viaTeam || isProjectWide) return;
    const { userId } = person;
    if (viaDirect) {
      setToRemove((prev) => {
        const next = new Set(prev);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(userId)) next.delete(userId);
        else next.set(userId, person);
        return next;
      });
    }
  }

  const selectedList = useMemo(() => [...selected.values()], [selected]);

  if (!open || !task) return null;

  const hasTeamAssigned = (task.teamIds ?? []).length > 0;

  return (
    <div className="dialog-backdrop">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-assignment-dialog-title"
        className="projects-dialog add-assignment-dialog"
      >
        <div className="add-assignment-dialog__head">
          <h2 id="add-assignment-dialog-title" className="dialog-title" style={{ marginBottom: 4 }}>
            Add people to task
          </h2>
          <p className="add-assignment-dialog__subtitle">
            <span>
              Adding to <strong>"{task.name}"</strong>
            </span>
            <span className="add-assignment-dialog__project-name"> · {project.name}</span>
          </p>
        </div>

        {hasTeamAssigned && (
          <div className="au-banner">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="currentColor"
              aria-hidden="true"
              style={{ flexShrink: 0, marginTop: 1 }}
            >
              <path d="M1 12c0-2.2 2-4 4.5-4h3C11 8 13 9.8 13 12M9.5 4a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z" />
            </svg>
            <div>
              This task already has a team assignment. Adding someone here pulls them in directly —
              useful for cross-team contributors.
            </div>
          </div>
        )}

        <div className="au-search">
          <span className="au-search__icon" aria-hidden="true">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <circle cx="6" cy="6" r="4" />
              <path d="M10 10l2.5 2.5" />
            </svg>
          </span>
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name across all teams…"
            aria-label="Search members"
          />
          {query && (
            <button
              type="button"
              className="au-search__clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M1 1l10 10M11 1L1 11" />
              </svg>
            </button>
          )}
        </div>

        <div className="au-list" role="listbox" aria-label="Members" aria-multiselectable="true">
          {isSearchMode ? (
            // Full user-population search
            <div className="au-group">
              {searchResult.isLoading && <div className="au-empty">Searching…</div>}
              {!searchResult.isLoading && (searchResult.data ?? []).length === 0 && (
                <div className="au-empty">
                  <div style={{ fontWeight: 600, color: 'var(--fg2)', marginBottom: 4 }}>
                    No one matches "{q}".
                  </div>
                  <div>Try a partial first or last name.</div>
                </div>
              )}
              {(searchResult.data ?? []).map((u) => {
                const memberRecord = membersById.get(u.userId);
                const viaTeam =
                  memberRecord?.teamId != null &&
                  (task.teamIds ?? []).includes(memberRecord.teamId);
                const viaDirect = !viaTeam && directAssignmentMap.has(u.userId);
                const isMarkedForRemoval = viaDirect && toRemove.has(u.userId);
                const isSel = selected.has(u.userId);
                const { bg, fg } = avatarColor(u.userId);
                const teamName = memberRecord?.teamId
                  ? (teamNameMap.get(memberRecord.teamId) ?? 'Team')
                  : null;
                return (
                  <AssignmentRow
                    key={u.userId}
                    userId={u.userId}
                    displayName={u.displayName}
                    initials={u.initials}
                    subLabel={teamName ?? u.email}
                    viaTeam={viaTeam}
                    viaDirect={viaDirect}
                    isProjectWide={false}
                    isMarkedForRemoval={isMarkedForRemoval}
                    isSel={isSel}
                    bg={bg}
                    fg={fg}
                    onToggle={toggle}
                  />
                );
              })}
            </div>
          ) : (
            // Project-member grouped view
            <>
              {membersQuery.isLoading && <div className="au-empty">Loading members…</div>}
              {groups.map(([teamId, people]) => {
                const teamName = teamId ? (teamNameMap.get(teamId) ?? 'Team') : 'Project lead';
                return (
                  <div key={teamId ?? '__lead'} className="au-group">
                    <div className="au-group-head">
                      <span className="au-group-head__name">{teamName}</span>
                      <span className="au-group-head__count">{people.length}</span>
                    </div>
                    {people.map((member) => {
                      const viaTeam =
                        member.teamId != null && (task.teamIds ?? []).includes(member.teamId);
                      const viaDirect = !viaTeam && member.onTaskIds.includes(task.taskId);
                      const isProjectWide =
                        viaDirect &&
                        totalTaskCount > 0 &&
                        member.onTaskIds.length >= totalTaskCount;
                      const isMarkedForRemoval =
                        viaDirect && !isProjectWide && toRemove.has(member.userId);
                      const isSel = selected.has(member.userId);
                      const { bg, fg } = avatarColor(member.userId);
                      return (
                        <AssignmentRow
                          key={member.userId}
                          userId={member.userId}
                          displayName={member.displayName}
                          initials={member.initials}
                          subLabel={teamName}
                          viaTeam={viaTeam}
                          viaDirect={viaDirect}
                          isProjectWide={isProjectWide}
                          isMarkedForRemoval={isMarkedForRemoval}
                          isSel={isSel}
                          bg={bg}
                          fg={fg}
                          onToggle={toggle}
                        />
                      );
                    })}
                  </div>
                );
              })}
              {!membersQuery.isLoading && members.length === 0 && (
                <div className="au-empty">No members on this project yet.</div>
              )}
            </>
          )}
        </div>

        <div className="dialog-footer au-footer">
          <div className="au-summary">
            {selected.size === 0 && toRemove.size === 0 ? (
              <span>Pick people to add, or click an added person to remove them.</span>
            ) : (
              <>
                {selected.size > 0 && (
                  <>
                    <strong>{selected.size}</strong>
                    <span> to add: </span>
                    <span className="au-chips">
                      {selectedList.slice(0, 3).map((m) => (
                        <span key={m.userId} className="au-chip">
                          <span aria-hidden="true">{m.initials}</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggle(m, false, false);
                            }}
                            aria-label={`Deselect ${m.displayName}`}
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              stroke="currentColor"
                              strokeWidth="1.8"
                              strokeLinecap="round"
                              aria-hidden="true"
                            >
                              <path d="M1 1l8 8M9 1L1 9" />
                            </svg>
                          </button>
                        </span>
                      ))}
                      {selected.size > 3 && (
                        <span className="au-chip au-chip--more">+{selected.size - 3}</span>
                      )}
                    </span>
                  </>
                )}
                {toRemove.size > 0 && (
                  <>
                    {selected.size > 0 && (
                      <span style={{ margin: '0 6px', color: 'var(--fg3)' }}>·</span>
                    )}
                    <strong style={{ color: 'var(--ut-bluebonnet)' }}>{toRemove.size}</strong>
                    <span> to remove</span>
                  </>
                )}
              </>
            )}
          </div>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={(selected.size === 0 && toRemove.size === 0) || isPending}
            aria-busy={isPending}
            onClick={() => {
              const toRemoveIds = [...toRemove]
                .map((uid) => directAssignmentMap.get(uid))
                .filter((id): id is string => id !== undefined);
              onSubmit([...selected.keys()], toRemoveIds);
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 11v-1a3 3 0 0 0-3-3H4a3 3 0 0 0-3 3v1" />
              <circle cx="5.5" cy="4" r="2" />
              <path d="M12 5v4M10 7h4" />
            </svg>
            {isPending
              ? 'Saving…'
              : selected.size > 0 && toRemove.size > 0
                ? 'Apply changes'
                : selected.size > 1
                  ? `Add ${selected.size} people`
                  : selected.size === 1
                    ? 'Add to task'
                    : toRemove.size > 1
                      ? `Remove ${toRemove.size} people`
                      : 'Remove from task'}
          </button>
        </div>
      </div>
    </div>
  );
}
